import React from 'react';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import useMajorGiftAutomations from '../hooks/useMajorGiftAutomations';

type AutomationsProviderContext = {
  closeForm: () => void;
  createAutomation: () => void;
  deleteAutomation: (automationId: string) => void;
  editAutomation: (automation: MajorGiftAutomation) => void;
  editingAutomation: MajorGiftAutomation | null;
  isFormActive: boolean;
};

const AutomationsContext = React.createContext(
  {} as AutomationsProviderContext,
);

export const useAutomationsContext = () => React.useContext(AutomationsContext);

function AutomationsProvider({ children }: { children: React.ReactNode }) {
  const [isFormActive, setIsFormActive] = React.useState(false);
  const [editingAutomation, setEditingAutomation] =
    React.useState<MajorGiftAutomation | null>(null);
  const { closeDialog, openDialog } = useDialogManager();
  const { remove } = useMajorGiftAutomations();

  const closeForm = React.useCallback(() => {
    setIsFormActive(false);
    setEditingAutomation(null);
  }, [setIsFormActive, setEditingAutomation]);

  const createAutomation = React.useCallback(() => {
    setIsFormActive(true);
    setEditingAutomation(null);
  }, [setIsFormActive, setEditingAutomation]);

  const deleteAutomation = React.useCallback(
    (automationId: string) => {
      openDialog(Dialogs.CONFIRMATION, {
        children: translate('AUTOMATIONS_DELETE_CONFIRMATION'),
        confirmLabel: translate('DELETE'),
        onCancel: closeDialog,
        onClose: closeDialog,
        onConfirm: async () => {
          await remove(automationId);
          closeDialog();
        },
      });
    },
    [closeDialog, openDialog],
  );

  const editAutomation = React.useCallback(
    (automation: MajorGiftAutomation) => {
      setIsFormActive(true);
      setEditingAutomation(automation);
    },
    [setIsFormActive, setEditingAutomation],
  );

  const value: AutomationsProviderContext = React.useMemo(
    () => ({
      closeForm,
      createAutomation,
      deleteAutomation,
      editAutomation,
      editingAutomation,
      isFormActive,
    }),
    [
      closeForm,
      createAutomation,
      deleteAutomation,
      editAutomation,
      editingAutomation,
      isFormActive,
    ],
  );
  return (
    <AutomationsContext.Provider value={value}>
      {children}
    </AutomationsContext.Provider>
  );
}

export default AutomationsProvider;
